import React, { useEffect, useRef, useState } from 'react'
import { ImagePanel } from '../../../email-composer/components/ImagePanel'
import { hapticTick, hapticSuccess } from '../../../../../../_utils/haptics'
import { SOCIAL_CLIENT_PHOTOS_VISIBLE } from '../../../../../../config/featureFlags'
import { STOCK_LIBRARY, photosFor } from '../../data/postTypes'
import { searchClients, loadClientPhotos, loadSalonPhotos, shareConsentOf, markShareable, photoTag } from '../../data/clientPhotos'
import { PHOTO_FILTERS } from '../../render/painter'
import { CloseIcon, PhotoIcon, ChevronLeft } from '../icons'

const Seg = ({ value, options, onChange, label }) => (
  <div className="sx-seg" role="radiogroup" aria-label={label}>
    {options.map(o => (
      <button key={o.id} type="button" role="radio" aria-checked={value === o.id} className={`sx-seg-btn${value === o.id ? ' is-on' : ''}`} onClick={() => { if (value !== o.id) { hapticTick(); onChange(o.id) } }}>
        {o.label}
      </button>
    ))}
  </div>
)

const small = (src) => (/images\.unsplash\.com/.test(src) ? `${src.split('?')[0]}?w=360&q=70&fm=jpg` : src)
const firstName = (s) => String(s || '').trim().split(/\s+/)[0] || 'They'

// ─── Client photos: recent ones across the salon, or find a client; only
// photos a client has said yes to can go on a post ───
const okLabel = (share) => (share === 'social' ? 'OK for social' : share === 'noFace' ? 'OK, face hidden' : 'Ask first')

const PhotoGrid = ({ photos, consent, want, onUse, onAsk }) => {
  // Before and after slots show photos tagged that way first
  const sorted = photos.slice().sort((a, b) => (want ? (photoTag(b) === want) - (photoTag(a) === want) : 0))
  return (
    <div className="sx-photo-grid">
      {sorted.map(p => {
        const share = shareConsentOf(p, consent)
        const ok = share !== 'none'
        const tag = photoTag(p)
        return (
          <button key={p.id} type="button" className={`sx-photo-tile${ok ? '' : ' is-locked'}`} onClick={() => { hapticTick(); if (ok) onUse(p); else onAsk(p) }} aria-label={ok ? 'Use this photo' : 'Ask before using this photo'}>
            <img src={p.thumb} alt="" loading="lazy" />
            {tag && <span className={`sx-photo-tag is-${tag}`}>{tag === 'before' ? 'Before' : 'After'}</span>}
            <span className={`sx-photo-ok${ok ? ' is-ok' : ''}`}>{okLabel(share)}</span>
          </button>
        )
      })}
    </div>
  )
}

const ClientPhotos = ({ slot, onPick }) => {
  const [q, setQ] = useState('')
  const [list, setList] = useState(null)
  const [client, setClient] = useState(null)
  const [photos, setPhotos] = useState(null)
  const [consent, setConsent] = useState({})
  const [recent, setRecent] = useState(null)
  const [recentCursor, setRecentCursor] = useState(null)
  const [loadingMore, setLoadingMore] = useState(false)
  const [asking, setAsking] = useState(null)
  const [error, setError] = useState('')
  const timer = useRef(null)
  const want = slot === 'before' || slot === 'after' ? slot : null

  // First view: the newest photos across the salon's clients
  useEffect(() => {
    let alive = true
    loadSalonPhotos({ slot })
      .then((r) => { if (alive) { setRecent(r.photos); setRecentCursor(r.nextCursor) } })
      .catch(() => { if (alive) setRecent([]) })
    return () => { alive = false }
  }, [slot])

  useEffect(() => {
    if (!q.trim()) { setList(null); return undefined }
    let alive = true
    clearTimeout(timer.current)
    timer.current = setTimeout(() => {
      searchClients(q.trim()).then((r) => { if (alive) setList(r.clients) }).catch(() => { if (alive) setList([]) })
    }, 250)
    return () => { alive = false; clearTimeout(timer.current) }
  }, [q])

  useEffect(() => {
    if (!client) return undefined
    let alive = true
    setPhotos(null)
    setError('')
    loadClientPhotos(client.id)
      .then((r) => { if (alive) { setPhotos(r.photos); setConsent(r.consent) } })
      .catch(() => { if (alive) { setPhotos([]); setError('Couldn’t load their photos. Check your connection and try again.') } })
    return () => { alive = false }
  }, [client])

  const moreRecent = () => {
    if (!recentCursor || loadingMore) return
    setLoadingMore(true)
    loadSalonPhotos({ slot, cursor: recentCursor })
      .then((r) => { setRecent(ps => [...(ps || []), ...r.photos]); setRecentCursor(r.nextCursor) })
      .catch(() => setError('Couldn’t load more photos. Try again.'))
      .finally(() => setLoadingMore(false))
  }

  const askName = asking ? firstName((asking.client && asking.client.name) || (client && client.name)) : ''
  const notice = asking && (
    <div className="sx-notice" role="dialog" aria-label="Sharing permission">
      <span>Has {askName} said yes to this photo going on social media?</span>
      <div className="sx-notice-actions">
        <button type="button" className="sx-btn" onClick={() => setAsking(null)}>Not yet</button>
        <button
          type="button"
          className="sx-btn sx-btn--dark"
          onClick={async () => {
            try {
              const ok = await markShareable(client ? client.id : null, asking)
              const swap = ps => (ps || []).map(p => (p.id === ok.id ? ok : p))
              setPhotos(swap)
              setRecent(swap)
              setAsking(null)
              hapticSuccess()
              onPick(ok.url)
            } catch (e) {
              setError(e && e.message && /consent/i.test(e.message)
                ? `${askName} hasn’t agreed to photos being kept on their record, so this one can’t be shared.`
                : 'Couldn’t save their permission. Try again.')
            }
          }}
        >
          Yes, they have
        </button>
      </div>
    </div>
  )
  const hint = <p className="sx-hint">Only photos a client has said yes to can go on a post. Say yes on their behalf only when they’ve told you.</p>

  if (client) {
    return (
      <div className="sx-clients">
        <button type="button" className="sx-back" onClick={() => { setClient(null); setAsking(null) }}><ChevronLeft size={16} /><span>{client.name}</span></button>
        {error && <p className="sx-error">{error}</p>}
        {!photos && <div className="sx-photo-grid">{[0, 1, 2, 3, 4, 5].map(i => <span key={i} className="sx-photo-tile is-loading" />)}</div>}
        {photos && !photos.length && !error && <p className="sx-empty">No photos on {firstName(client.name)}’s record yet. Photos taken in the diary or on their profile show here.</p>}
        {notice}
        {photos && photos.length > 0 && <PhotoGrid photos={photos} consent={consent} want={want} onUse={p => onPick(p.url)} onAsk={setAsking} />}
        {hint}
      </div>
    )
  }

  return (
    <div className="sx-clients">
      <input className="sx-input" type="search" placeholder="Search clients by name" value={q} onChange={(e) => { const v = e.target.value; setQ(v) }} aria-label="Search clients" />
      {q.trim() ? (
        <>
          {!list && <div className="sx-client-list">{[0, 1, 2, 3].map(i => <span key={i} className="sx-client-row is-loading" />)}</div>}
          {list && !list.length && <p className="sx-empty">{`No clients called “${q}”.`}</p>}
          {list && list.length > 0 && (
            <div className="sx-client-list">
              {list.map(c => (
                <button key={c.id} type="button" className="sx-client-row" onClick={() => { hapticTick(); setAsking(null); setClient(c) }}>
                  <span className="sx-client-avatar" aria-hidden>{c.avatar ? <img src={c.avatar} alt="" /> : c.name[0]}</span>
                  <span className="sx-client-name">{c.name}</span>
                </button>
              ))}
            </div>
          )}
        </>
      ) : (
        <>
          <span className="sx-label">{want ? `Recent ${want} photos` : 'Recent client photos'}</span>
          {error && <p className="sx-error">{error}</p>}
          {!recent && <div className="sx-photo-grid">{[0, 1, 2, 3, 4, 5].map(i => <span key={i} className="sx-photo-tile is-loading" />)}</div>}
          {recent && !recent.length && <p className="sx-empty">{want ? `No ${want} photos yet. ` : 'No client photos yet. '}Photos taken in the diary or on a client’s profile show here, or search for a client.</p>}
          {notice}
          {recent && recent.length > 0 && <PhotoGrid photos={recent} want={want} onUse={p => onPick(p.url)} onAsk={setAsking} />}
          {recentCursor && <button type="button" className="sx-link" onClick={moreRecent} disabled={loadingMore}>{loadingMore ? 'Loading…' : 'Show more'}</button>}
          {hint}
        </>
      )}
    </div>
  )
}

// ─── Where a photo comes from ───
export const PhotoPicker = ({ trade, salon, slot, allowStock, onPick, onClose }) => {
  const [tab, setTab] = useState(slot === 'before' || slot === 'after' ? (SOCIAL_CLIENT_PHOTOS_VISIBLE ? 'clients' : 'yours') : 'yours')
  useEffect(() => {
    const onKey = (e) => { if (e.key === 'Escape') { e.stopPropagation(); onClose() } }
    window.addEventListener('keydown', onKey, true)
    return () => window.removeEventListener('keydown', onKey, true)
  }, [onClose])
  const tabs = [
    { id: 'yours', label: 'Your photos' },
    ...(SOCIAL_CLIENT_PHOTOS_VISIBLE ? [{ id: 'clients', label: 'Client photos' }] : []),
    ...(allowStock ? [{ id: 'stock', label: 'Stock' }] : []),
  ]
  return (
    <div className="sx-sheet-backdrop" onPointerDown={(e) => { if (e.target === e.currentTarget) onClose() }}>
      <div className="sx-sheet sx-photo-sheet" role="dialog" aria-modal="true" aria-label="Choose a photo">
        <div className="sx-sheet-head">
          <h2 className="sx-sheet-title">Choose a photo</h2>
          <button type="button" className="sx-icon-btn" onClick={onClose} aria-label="Close"><CloseIcon /></button>
        </div>
        {tabs.length > 1 && <Seg label="Photo source" value={tab} onChange={setTab} options={tabs} />}
        <div className="sx-photo-body">
          {tab === 'yours' && <ImagePanel salon={salon} onSelect={(url) => onPick(url)} />}
          {tab === 'clients' && <ClientPhotos slot={slot} onPick={onPick} />}
          {tab === 'stock' && (
            <>
              <p className="sx-hint">Your own photos always do better. These are here for when you haven’t got one.</p>
              <div className="sx-stock-grid">
                {(STOCK_LIBRARY[trade] || STOCK_LIBRARY.hair).map(src => (
                  <button key={src} type="button" className="sx-stock" onClick={() => onPick(src)} aria-label="Use this photo">
                    <img src={small(src)} alt="" loading="lazy" />
                  </button>
                ))}
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  )
}

// The post's photos: change, zoom, a filter, back to the middle
const PhotosPanel = ({ post, activeSlot, onPickSlot, onMovePhoto }) => {
  const slots = photosFor(post)
  if (!slots.length) return <p className="sx-hint">This design is all words, so there’s no photo to add.</p>
  return (
    <section className="sx-group">
      {slots.map(s => {
        const ph = (post.photos || {})[s.key] || {}
        const on = activeSlot === `photo:${s.key}`
        return (
          <div key={s.key} id={`sx-photo-${s.key}`} className={`sx-photo-row${on ? ' is-on' : ''}`}>
            <div className="sx-photo-top">
              <button type="button" className="sx-photo-thumb" onClick={() => onPickSlot(s.key)} aria-label={`Change ${s.label.toLowerCase()} photo`}>
                {ph.src ? <img src={small(ph.src)} alt="" /> : <PhotoIcon />}
              </button>
              <div className="sx-photo-meta">
                <span className="sx-photo-label">{s.label}</span>
                <button type="button" className="sx-link" onClick={() => onPickSlot(s.key)}>{ph.src ? 'Change photo' : 'Add a photo'}</button>
              </div>
            </div>
            {ph.src && (
              <>
                <label className="sx-zoom">
                  <span className="sx-label">Zoom</span>
                  <input type="range" min="1" max="2.5" step="0.05" value={ph.zoom || 1} onChange={(e) => { const z = Number(e.target.value); onMovePhoto(s.key, { zoom: z }, `zoom-${s.key}`) }} />
                </label>
                <div className="sx-filter-row" role="radiogroup" aria-label={`${s.label} filter`}>
                  {PHOTO_FILTERS.map(f => (
                    <button key={f.name} type="button" role="radio" aria-checked={(ph.filter || null) === f.id} className={`sx-chip${(ph.filter || null) === f.id ? ' is-on' : ''}`} onClick={() => { hapticTick(); onMovePhoto(s.key, { filter: f.id || undefined }, `filter-${s.key}`) }}>
                      {f.name}
                    </button>
                  ))}
                </div>
                {(ph.fx !== 0.5 || ph.fy !== 0.4 || (ph.zoom || 1) !== 1) && (
                  <button type="button" className="sx-link sx-reset" onClick={() => { hapticTick(); onMovePhoto(s.key, { fx: 0.5, fy: 0.4, zoom: 1 }, `reset-${s.key}`) }}>Back to the middle</button>
                )}
              </>
            )}
          </div>
        )
      })}
      <p className="sx-hint">{post.type === 'beforeAfter'
        ? 'Use two photos of the same client, taken from a similar angle, and only when they’ve said yes to social media.'
        : 'Drag the photo on the post to move it.'}</p>
    </section>
  )
}

export default PhotosPanel
